import React, { useEffect, useState } from "react";
import { Button, Modal, message, Tag, Select } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import {
    PlusOutlined,
    DeleteOutlined,
    BellOutlined,
    CheckOutlined,
    EyeOutlined,
    MailOutlined,
    AlertOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import Paginationnew from "../../Uitiles/Paginationnew";

const BloodBankNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [filterType, setFilterType] = useState('all');

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        type: 'info',
        priority: 'normal'
    });

    const notificationTypes = ['info', 'warning', 'alert', 'success'];
    const priorityOptions = ['low', 'normal', 'high', 'urgent'];

    useEffect(() => {
        loadNotifications();
    }, [page, searchText, filterType]);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            let conditions = { blood_bank_id: userId };

            if (filterType !== 'all') {
                conditions.type = filterType;
            }

            const search = searchText ? {
                title: searchText,
                message: searchText
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'blood_bank_notifications', conditions, 'id DESC', search, pagination);
            if (response && response.data) {
                setNotifications(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
            message.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!formData.title || !formData.message) {
                message.error('Please fill in all required fields');
                return;
            }

            const userId = await getuserdata('id');
            const notificationData = {
                ...formData,
                blood_bank_id: userId,
                is_read: false,
                created_at: new Date().toISOString()
            };

            await oneKlicks.create('', 'blood_bank_notifications', notificationData);
            message.success('Notification created successfully');

            setModalVisible(false);
            resetForm();
            loadNotifications();
        } catch (error) {
            console.error('Error saving notification:', error);
            message.error('Failed to save notification');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            message: '',
            type: 'info',
            priority: 'normal'
        });
    };

    const handleView = async (notification) => {
        setSelectedNotification(notification);
        setViewModal(true);

        // Mark as read
        if (!notification.is_read) {
            try {
                await oneKlicks.update('', 'blood_bank_notifications', { is_read: true }, { id: notification.id });
                loadNotifications();
            } catch (error) {
                console.error('Error marking as read:', error);
            }
        }
    };

    const handleMarkAsRead = async (notification) => {
        try {
            await oneKlicks.update('', 'blood_bank_notifications', { is_read: true }, { id: notification.id });
            message.success('Marked as read');
            loadNotifications();
        } catch (error) {
            console.error('Error marking as read:', error);
            message.error('Failed to mark as read');
        }
    };

    const handleDelete = async (notification) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this notification?`);
        if (confirmDelete) {
            try {
                await oneKlicks.delete('', 'blood_bank_notifications', { id: notification.id });
                message.success('Notification deleted successfully');
                loadNotifications();
            } catch (error) {
                console.error('Error deleting notification:', error);
                message.error('Failed to delete notification');
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const getTypeColor = (type) => {
        const colors = {
            info: 'blue',
            warning: 'orange',
            alert: 'red',
            success: 'green'
        };
        return colors[type] || 'default';
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'alert': return <AlertOutlined />;
            case 'warning': return <AlertOutlined />;
            case 'success': return <CheckOutlined />;
            default: return <BellOutlined />;
        }
    };

    const getPriorityBadge = (priority) => {
        const colors = {
            low: 'secondary',
            normal: 'info',
            high: 'warning',
            urgent: 'danger'
        };
        return colors[priority] || 'info';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">
                                    <BellOutlined className="me-2" />
                                    Notifications
                                </h5>
                                <Badge bg="primary">Notifications</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40" style={{ maxWidth: '300px' }}>
                                    <Form.Control
                                        placeholder="Search notifications..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </InputGroup>
                                <Select
                                    style={{ width: 150 }}
                                    value={filterType}
                                    onChange={setFilterType}
                                    options={[
                                        { value: 'all', label: 'All Types' },
                                        { value: 'info', label: 'Info' },
                                        { value: 'warning', label: 'Warning' },
                                        { value: 'alert', label: 'Alert' },
                                        { value: 'success', label: 'Success' }
                                    ]}
                                />
                                <Button className="btn-primary h-40" onClick={() => {
                                    resetForm();
                                    setModalVisible(true);
                                }}>
                                    <PlusOutlined /> Create Notification
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}></th>
                                        <th>Title</th>
                                        <th>Type</th>
                                        <th>Priority</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th style={{ width: '150px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : notifications.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">No notifications found</td>
                                        </tr>
                                    ) : (
                                        notifications.map((item) => (
                                            <tr key={item.id} style={{ backgroundColor: item.is_read ? 'transparent' : '#f6ffed' }}>
                                                <td>
                                                    <span style={{
                                                        fontSize: '20px',
                                                        color: getTypeColor(item.type) === 'blue' ? '#1890ff' :
                                                            getTypeColor(item.type) === 'orange' ? '#faad14' :
                                                                getTypeColor(item.type) === 'red' ? '#ff4d4f' : '#52c41a'
                                                    }}>
                                                        {getTypeIcon(item.type)}
                                                    </span>
                                                </td>
                                                <td data-label="Title">
                                                    <div>
                                                        <strong style={{ fontWeight: item.is_read ? 'normal' : 'bold' }}>
                                                            {item.title}
                                                        </strong>
                                                        <div style={{ fontSize: '12px', color: '#666', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {item.message}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td data-label="Type">
                                                    <Tag color={getTypeColor(item.type)}>
                                                        {item.type?.toUpperCase() || 'INFO'}
                                                    </Tag>
                                                </td>
                                                <td data-label="Priority">
                                                    <Badge bg={getPriorityBadge(item.priority)}>
                                                        {item.priority?.toUpperCase() || 'NORMAL'}
                                                    </Badge>
                                                </td>
                                                <td data-label="Date">
                                                    {formatDate(item.created_at)}
                                                </td>
                                                <td data-label="Status">
                                                    {item.is_read ? (
                                                        <Tag color="default">Read</Tag>
                                                    ) : (
                                                        <Tag color="green">New</Tag>
                                                    )}
                                                </td>
                                                <td data-label="Actions">
                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                        <Button
                                                            className="btn-info"
                                                            onClick={() => handleView(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <EyeOutlined />
                                                        </Button>
                                                        {!item.is_read && (
                                                            <Button
                                                                className="btn-success"
                                                                onClick={() => handleMarkAsRead(item)}
                                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                            >
                                                                <CheckOutlined />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            className="btn-danger"
                                                            onClick={() => handleDelete(item)}
                                                            style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        >
                                                            <DeleteOutlined />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Create Notification Modal */}
                <Modal
                    title="Create Notification"
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        resetForm();
                    }}
                    onOk={handleSubmit}
                    width={600}
                >
                    <div className="row">
                        <div className="col-12 mb-3">
                            <label className="form-label">Title *</label>
                            <Form.Control
                                placeholder="Enter notification title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Type</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.type}
                                onChange={(value) => setFormData({ ...formData, type: value })}
                                options={notificationTypes.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Priority</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.priority}
                                onChange={(value) => setFormData({ ...formData, priority: value })}
                                options={priorityOptions.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
                            />
                        </div>
                        <div className="col-12 mb-3">
                            <label className="form-label">Message *</label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter notification message..."
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            />
                        </div>
                    </div>
                </Modal>

                {/* View Notification Modal */}
                <Modal
                    title={<><BellOutlined className="me-2" />Notification Details</>}
                    open={viewModal}
                    onCancel={() => setViewModal(false)}
                    footer={[
                        <Button key="close" onClick={() => setViewModal(false)}>Close</Button>
                    ]}
                    width={600}
                >
                    {selectedNotification && (
                        <div>
                            <div className="mb-3">
                                <Tag color={getTypeColor(selectedNotification.type)}>
                                    {selectedNotification.type?.toUpperCase()}
                                </Tag>
                                <Badge bg={getPriorityBadge(selectedNotification.priority)} className="ms-2">
                                    {selectedNotification.priority?.toUpperCase()}
                                </Badge>
                            </div>
                            <h5>{selectedNotification.title}</h5>
                            <p style={{ color: '#666', fontSize: '12px' }}>
                                {formatDate(selectedNotification.created_at)}
                            </p>
                            <div style={{
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {selectedNotification.message}
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default BloodBankNotifications;
